import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowUpRight,
  ChevronDown,
  ChevronRight,
  GitBranch,
  Loader2,
  Search,
  Users
} from 'lucide-react';
import {
  getLeadProgression,
  getLeadProgressionItems
} from '../../services/api/implementation/impl';
import { navigateWithReturn } from '../../../shared/navigation';

function progressionItemPath(item) {
  const leadId = Number(item?.lead_id || (item?.kind === 'lead' ? item?.id : 0));
  if (Number.isFinite(leadId) && leadId > 0) {
    return `/builder/sales/${leadId}`;
  }

  const listId = Number(item?.pre_lead_list_id);
  if (Number.isFinite(listId) && listId > 0) {
    const q = new URLSearchParams();
    const searchHint = String(item?.phone || item?.name || '').trim();
    if (searchHint) q.set('search', searchHint);
    const qs = q.toString();
    return `/builder/sales/prelead-lists/${listId}${qs ? `?${qs}` : ''}`;
  }

  return null;
}

function istYmd(d = new Date()) {
  return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}

function defaultFromDate() {
  const today = istYmd();
  return `${today.slice(0, 7)}-01`;
}

function defaultToDate() {
  return istYmd();
}

const STAGE_THEME = {
  unqualified: {
    bar: 'bg-slate-700',
    chip: 'bg-slate-100 text-slate-800',
    chevron: 'bg-slate-100 text-slate-700',
    track: 'bg-slate-100'
  },
  qualified: {
    bar: 'bg-sky-500',
    chip: 'bg-sky-50 text-sky-700',
    chevron: 'bg-sky-50 text-sky-600',
    track: 'bg-sky-50'
  },
  cold: {
    bar: 'bg-teal-500',
    chip: 'bg-teal-50 text-teal-700',
    chevron: 'bg-teal-50 text-teal-600',
    track: 'bg-teal-50'
  },
  demo: {
    bar: 'bg-violet-500',
    chip: 'bg-violet-50 text-violet-700',
    chevron: 'bg-violet-50 text-violet-600',
    track: 'bg-violet-50'
  },
  warm: {
    bar: 'bg-orange-400',
    chip: 'bg-orange-50 text-orange-700',
    chevron: 'bg-orange-50 text-orange-600',
    track: 'bg-orange-50'
  },
  hot: {
    bar: 'bg-rose-500',
    chip: 'bg-rose-50 text-rose-700',
    chevron: 'bg-rose-50 text-rose-600',
    track: 'bg-rose-50'
  },
  closed_won: {
    bar: 'bg-emerald-500',
    chip: 'bg-emerald-50 text-emerald-700',
    chevron: 'bg-emerald-50 text-emerald-600',
    track: 'bg-emerald-50'
  },
  closed_lost: {
    bar: 'bg-red-800',
    chip: 'bg-red-50 text-red-800',
    chevron: 'bg-red-50 text-red-800',
    track: 'bg-red-50'
  }
};

function stageTheme(key) {
  return STAGE_THEME[key] || STAGE_THEME.unqualified;
}

/**
 * Metric-tile opener for Lead Progression Flow (fullscreen overlay).
 */
export function LeadProgressionFlowCard({
  projectId,
  salesUsers = [],
  isSalesLow = false,
  currentUserId = null,
  className = ''
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`group relative h-[92px] w-full overflow-hidden rounded-xl border border-teal-100 bg-gradient-to-br from-teal-50 via-white to-slate-50 px-4 text-left shadow-sm transition hover:shadow-md ${className}`}>
        <div className="flex h-full items-center gap-3">
          <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-teal-100 text-teal-800">
            <GitBranch className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-teal-800">
              Progression Flow
            </p>
            <p className="mt-0.5 truncate text-sm font-semibold text-gray-900">
              Stage-entry counts
            </p>
            <p className="mt-0.5 truncate text-[11px] text-gray-500">
              Leads that moved into a stage
            </p>
          </div>
        </div>
        <span className="absolute bottom-2 right-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-teal-700/10 text-teal-700 transition group-hover:bg-teal-700/20">
          <ArrowUpRight size={14} />
        </span>
      </button>

      {open ? (
        <LeadProgressionFlowView
          projectId={projectId}
          salesUsers={salesUsers}
          isSalesLow={isSalesLow}
          currentUserId={currentUserId}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

function StageRowDetails({
  projectId,
  stageKey,
  salesUserId,
  from,
  to,
  search
}) {
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: [
      'leadProgressionItems',
      projectId,
      stageKey,
      salesUserId || 'all',
      from,
      to,
      search || ''
    ],
    queryFn: () =>
      getLeadProgressionItems(projectId, stageKey, {
        salesUserId: salesUserId || undefined,
        from,
        to,
        q: search || undefined,
        limit: 40
      }),
    enabled: !!projectId && !!stageKey && !!from && !!to,
    staleTime: 60_000
  });

  const items = data?.items || [];
  const total = Number(data?.total ?? items.length) || 0;

  const openItem = (item) => {
    const path = progressionItemPath(item);
    if (!path) return;
    navigateWithReturn(navigate, path, {
      state: {
        fromProgressionFlow: true,
        progressionKind: item?.kind || null
      }
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-6 text-xs text-gray-500">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading…
      </div>
    );
  }

  if (isError) {
    return (
      <p className="px-3 py-4 text-xs text-rose-600">
        {error?.message || 'Failed to load details'}
      </p>
    );
  }

  if (items.length === 0) {
    return (
      <p className="px-3 py-5 text-center text-xs text-gray-500">
        {search
          ? 'No matching leads in this stage.'
          : 'No leads moved into this stage in the selected range.'}
      </p>
    );
  }

  return (
    <div className="px-1 pb-1 sm:px-2">
      <div className="max-h-[min(22rem,45vh)] overflow-auto rounded-xl border border-gray-100">
        <table className="min-w-full text-left text-sm">
          <thead className="sticky top-0 z-[1] bg-gray-50/95 backdrop-blur">
            <tr className="text-[11px] uppercase tracking-wide text-gray-500">
              <th className="px-3 py-2.5 font-semibold sm:px-4">Name</th>
              <th className="px-3 py-2.5 font-semibold sm:px-4">Phone</th>
              <th className="hidden px-3 py-2.5 font-semibold sm:table-cell sm:px-4">
                Email
              </th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {items.map((item) => {
              const path = progressionItemPath(item);
              const clickable = Boolean(path);
              return (
                <tr
                  key={`${item.kind}-${item.id}`}
                  role={clickable ? 'link' : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onClick={() => {
                    if (clickable) openItem(item);
                  }}
                  onKeyDown={(e) => {
                    if (!clickable) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openItem(item);
                    }
                  }}
                  title={clickable ? 'Open lead / pre-lead' : undefined}
                  className={`border-t border-gray-50 align-top transition-colors ${
                    clickable
                      ? 'cursor-pointer hover:bg-teal-50/60 focus-visible:bg-teal-50/60 focus-visible:outline-none'
                      : ''
                  }`}>
                  <td className="px-3 py-2.5 font-semibold text-gray-900 sm:px-4">
                    <div className={clickable ? 'text-teal-800' : undefined}>
                      {item.name || '—'}
                    </div>
                    <div className="mt-0.5 break-all text-xs font-normal text-gray-500 sm:hidden">
                      {item.email || '—'}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-gray-700 sm:px-4">
                    {item.phone || '—'}
                  </td>
                  <td className="hidden break-all px-3 py-2.5 text-gray-700 sm:table-cell sm:px-4">
                    {item.email || '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="px-1 pt-2 text-[11px] text-gray-500">
        Showing {items.length} of {total}
      </p>
    </div>
  );
}

/**
 * Fullscreen portal: custom date + sales user filters, collapsible stage rows.
 */
export function LeadProgressionFlowView({
  projectId,
  salesUsers = [],
  isSalesLow = false,
  currentUserId = null,
  onClose
}) {
  const [visible, setVisible] = useState(false);
  const [from, setFrom] = useState(defaultFromDate);
  const [to, setTo] = useState(defaultToDate);
  const [salesUserId, setSalesUserId] = useState(
    isSalesLow && currentUserId ? String(currentUserId) : ''
  );
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [expandedKey, setExpandedKey] = useState(null);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => onClose?.(), 180);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setVisible(false);
        setTimeout(() => onClose?.(), 180);
      }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const querySalesUserId = useMemo(() => {
    if (isSalesLow && currentUserId) return String(currentUserId);
    return salesUserId || undefined;
  }, [isSalesLow, currentUserId, salesUserId]);

  const selectedSalesLabel = useMemo(() => {
    if (isSalesLow) return 'You';
    if (!salesUserId) return 'All users';
    const match = salesUsers.find((u) => String(u.id) === String(salesUserId));
    return match?.name || 'Selected user';
  }, [isSalesLow, salesUserId, salesUsers]);

  const dateReady = Boolean(from && to);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: [
      'leadProgression',
      projectId,
      querySalesUserId || 'all',
      from,
      to
    ],
    queryFn: () =>
      getLeadProgression(projectId, {
        salesUserId: querySalesUserId,
        from,
        to
      }),
    enabled: !!projectId && dateReady,
    staleTime: 60_000
  });

  const stages = data?.stages || [];
  const maxCount = useMemo(
    () => Math.max(1, ...stages.map((s) => Number(s.count ?? 0) || 0)),
    [stages]
  );
  const totalLeads = useMemo(
    () => stages.reduce((sum, s) => sum + (Number(s.count ?? 0) || 0), 0),
    [stages]
  );

  const content = (
    <div className="fixed inset-0 z-[1000] flex flex-col bg-[#F3F4F6]">
      <div
        className={`flex h-full min-h-0 flex-col transition-opacity duration-200 ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}>
        <header className="sticky top-0 z-10 border-b border-gray-200/80 bg-white/95 px-3 py-3 backdrop-blur sm:px-6 sm:py-4">
          <div className="mx-auto flex w-full max-w-5xl flex-col gap-3">
            <div className="flex items-start gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-sm transition hover:bg-gray-50"
                aria-label="Back">
                <ArrowLeft className="h-4 w-4" />
              </button>
              <div className="min-w-0 flex-1">
                <h2 className="truncate text-base font-bold text-gray-900 sm:text-xl">
                  Lead Progression Flow
                </h2>
                <p className="truncate text-xs text-gray-500 sm:text-sm">
                  Stage-entry counts · {selectedSalesLabel}
                </p>
              </div>
              <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-violet-100 px-2.5 py-1 text-xs font-semibold text-violet-800 sm:px-3 sm:text-sm">
                <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                {isLoading ? '…' : `${totalLeads.toLocaleString('en-IN')} leads`}
              </span>
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {!isSalesLow ? (
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 sm:text-[11px]">
                    Sales user
                  </span>
                  <select
                    value={salesUserId}
                    onChange={(e) => {
                      setSalesUserId(e.target.value);
                      setExpandedKey(null);
                    }}
                    className="h-10 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-800 shadow-sm">
                    <option value="">All users</option>
                    {salesUsers.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 sm:text-[11px]">
                  From
                </span>
                <input
                  type="date"
                  value={from}
                  onChange={(e) => {
                    setFrom(e.target.value);
                    setExpandedKey(null);
                  }}
                  className="h-10 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-800 shadow-sm"
                />
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 sm:text-[11px]">
                  To
                </span>
                <input
                  type="date"
                  value={to}
                  onChange={(e) => {
                    setTo(e.target.value);
                    setExpandedKey(null);
                  }}
                  className="h-10 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-800 shadow-sm"
                />
              </label>

              <label
                className={`flex flex-col gap-1 ${
                  isSalesLow ? 'sm:col-span-2 lg:col-span-1' : ''
                }`}>
                <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 sm:text-[11px]">
                  Search lead
                </span>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="search"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Name, phone or email"
                    className="h-10 w-full rounded-xl border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-800 shadow-sm placeholder:text-gray-400"
                  />
                </div>
              </label>
            </div>
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4 sm:px-6 sm:py-5">
          <div className="mx-auto w-full max-w-5xl">
            {!projectId ? (
              <p className="rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-10 text-center text-sm text-gray-500">
                Select a project on the leads page to view progression.
              </p>
            ) : !dateReady ? (
              <p className="rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-10 text-center text-sm text-gray-500">
                Choose a custom from and to date.
              </p>
            ) : isLoading ? (
              <div className="flex items-center justify-center gap-2 py-20 text-sm text-gray-500">
                <Loader2 className="h-5 w-5 animate-spin" />
                Loading progression…
              </div>
            ) : isError ? (
              <p className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-8 text-center text-sm text-rose-700">
                {error?.message || 'Failed to load lead progression'}
              </p>
            ) : (
              <div className="space-y-2.5">
                {isFetching && !isLoading ? (
                  <p className="text-right text-[11px] text-gray-400">Updating…</p>
                ) : null}
                {stages.map((row) => {
                  const open = expandedKey === row.key;
                  const count = Number(row.count ?? 0) || 0;
                  const theme = stageTheme(row.key);
                  const pct = Math.max(
                    count > 0 ? 4 : 0,
                    Math.round((count / maxCount) * 100)
                  );
                  return (
                    <div
                      key={row.key}
                      className={`overflow-hidden rounded-2xl border bg-white shadow-sm transition ${
                        open
                          ? 'border-gray-300 ring-1 ring-gray-200'
                          : 'border-gray-200'
                      }`}>
                      <button
                        type="button"
                        onClick={() => setExpandedKey(open ? null : row.key)}
                        className="flex w-full items-center gap-3 px-3 py-3 text-left sm:gap-4 sm:px-4 sm:py-3.5">
                        <span
                          className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${theme.chevron}`}>
                          {open ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-3">
                            <span className="truncate text-sm font-bold text-gray-900 sm:text-[15px]">
                              {row.label}
                            </span>
                            <span
                              className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-bold tabular-nums sm:text-sm ${theme.chip}`}>
                              {count.toLocaleString('en-IN')}
                            </span>
                          </div>
                          <div
                            className={`mt-2 h-1.5 w-full overflow-hidden rounded-full ${theme.track}`}>
                            <div
                              className={`h-full rounded-full transition-all duration-300 ${theme.bar}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      </button>
                      {open ? (
                        <div className="border-t border-gray-100 bg-white px-2 pb-3 pt-2 sm:px-3">
                          <StageRowDetails
                            projectId={projectId}
                            stageKey={row.key}
                            salesUserId={querySalesUserId}
                            from={from}
                            to={to}
                            search={search}
                          />
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

export default LeadProgressionFlowCard;
